import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MilestoneMoveDirection,
  MilestonePhaseItemRequestDto,
} from 'src/api/FiretrolApiClient';
import { milestonePhaseItemApiClient } from 'src/api/FiretrolApiClientInstances';
import { invalidateFor } from 'src/api/queries/invalidationMap';

/** Item writes target either a job or, when `jobId` is absent, a milestone template. */
export interface MilestoneItemScope {
  jobId?: number;
  templateId?: string;
}

const scopeKey = (scope: MilestoneItemScope) =>
  String(scope.jobId ?? 'default');

const invalidate = (
  qc: ReturnType<typeof useQueryClient>,
  scope: MilestoneItemScope,
) =>
  scope.jobId != null
    ? invalidateFor('milestoneItem.save', qc, { jobId: scope.jobId })
    : invalidateFor('milestoneItem.saveForTemplate', qc, {
        templateId: scope.templateId,
      });

export function useCreateMilestoneItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      scope,
      request,
    }: {
      scope: MilestoneItemScope;
      request: MilestonePhaseItemRequestDto;
    }) => milestonePhaseItemApiClient.createPhaseItem(scopeKey(scope), request),
    meta: {
      successMessage: 'Milestone item successfully created!',
      suppressErrorToast: true,
    },
    onSettled: (_data, _err, { scope }) => invalidate(qc, scope),
  });
}

export function useUpdateMilestoneItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      scope,
      itemId,
      request,
    }: {
      scope: MilestoneItemScope;
      itemId: string;
      request: MilestonePhaseItemRequestDto;
    }) =>
      milestonePhaseItemApiClient.updatePhaseItem(
        scopeKey(scope),
        itemId,
        request,
      ),
    meta: {
      successMessage: 'Milestone item updated!',
      suppressErrorToast: true,
    },
    onSettled: (_data, _err, { scope }) => invalidate(qc, scope),
  });
}

export function useDeleteMilestoneItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId }: { scope: MilestoneItemScope; itemId: string }) =>
      milestonePhaseItemApiClient.deletePhaseItem(itemId),
    meta: { successMessage: 'Milestone item removed!' },
    onSettled: (_data, _err, { scope }) => invalidate(qc, scope),
  });
}

export function useToggleMilestoneItemComplete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId }: { scope: MilestoneItemScope; itemId: string }) =>
      milestonePhaseItemApiClient.togglePhaseItemComplete(itemId),
    onSettled: (_data, _err, { scope }) => invalidate(qc, scope),
  });
}

export function useMoveMilestoneItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      scope,
      itemId,
      direction,
    }: {
      scope: MilestoneItemScope;
      itemId: string;
      direction: MilestoneMoveDirection;
    }) =>
      milestonePhaseItemApiClient.movePhaseItem(scopeKey(scope), itemId, {
        direction,
      }),
    meta: { errorContext: 'milestone order' },
    onSettled: (_data, _err, { scope }) => invalidate(qc, scope),
  });
}
